import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CaretRight, X } from '@phosphor-icons/react';
import {
  isTabActive,
  matchGroupId,
  rememberSection,
  type GroupId,
  type VisibleNav,
} from '../../lib/nav';

export interface PhoneMenuProps {
  open: boolean;
  onClose: () => void;
  nav: VisibleNav;
  user?: { email?: string; companyName?: string; contactName?: string };
  /** Acting-client selector (renders nothing for single-client users). */
  clientSelector?: ReactNode;
  onLogout: () => void;
  onToggleLanguage: () => void;
  onOpenHelp: () => void;
  onReportBug: () => void;
}

/**
 * Full-screen phone menu (below the lg breakpoint), per the approved board:
 * navy bar, client selector, four group tabs (Dashboard, Operations, Shipping,
 * Returns), sections with page chips, account footer with language toggle and
 * Admin / Help / Report bug / Log out.
 */
export default function PhoneMenu({
  open,
  onClose,
  nav,
  user,
  clientSelector,
  onLogout,
  onToggleLanguage,
  onOpenHelp,
  onReportBug,
}: PhoneMenuProps) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // The four group tabs; Dashboard is one of them and goes straight to its page.
  const tabGroups = nav.groups;
  const activeGroup = matchGroupId(pathname);
  const [tab, setTab] = useState<GroupId | null>(null);
  const firstWithSections = tabGroups.find((g) => g.sections.length > 0)?.id ?? null;
  const currentTab: GroupId | null =
    tab && tabGroups.some((g) => g.id === tab)
      ? tab
      : tabGroups.find((g) => g.id === activeGroup && g.sections.length > 0)?.id ?? firstWithSections;

  // Open on the tab of the page you are on.
  useEffect(() => {
    if (open) setTab(null);
  }, [open]);

  // Keep the latest onClose without re-running the focus effect on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus management + Escape + scroll lock.
  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // The page behind is inert: no focus, no clicks, hidden from assistive tech.
    const appRoot = document.getElementById('root');
    const wasInert = appRoot?.hasAttribute('inert') ?? false;
    appRoot?.setAttribute('inert', '');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = Array.from(
        dialog.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => el.getAttribute('tabindex') !== '-1');
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (!active || !dialog.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (!wasInert) appRoot?.removeAttribute('inert');
      returnFocusRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const shown = tabGroups.find((g) => g.id === currentTab);
  const initial = (user?.contactName || user?.companyName || user?.email || 'U').charAt(0).toUpperCase();
  const footerLink =
    'inline-flex h-10 items-center justify-center rounded-lg bg-slate-50 text-[12.5px] text-slate-700 hover:bg-slate-100';
  const lang = i18n.language === 'fr' ? 'fr' : 'en';

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('nav.menu')}
      data-testid="phone-menu"
      className="fixed inset-0 z-drawer flex flex-col bg-white lg:hidden"
    >
      <div className="primary-nav flex h-14 flex-none items-center gap-2.5 pl-4 pr-2">
        <img src="/logo.png?v=2" alt="Arivo" className="h-7 w-auto" />
        <span className="text-[15px] font-semibold text-white">{t('nav.menu')}</span>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t('nav.closeMenu')}
          data-testid="phone-menu-close"
          className="ml-auto flex h-11 w-11 items-center justify-center text-white hover:bg-white/10"
        >
          <X className="h-[22px] w-[22px]" weight="regular" />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        {clientSelector && (
          <div className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600" data-testid="phone-menu-client">
            <span>{t('nav.client')}</span>
            {clientSelector}
          </div>
        )}

        <div role="group" aria-label={t('nav.groups')} className="grid gap-1 rounded-xl bg-slate-100 p-1" style={{ gridTemplateColumns: `repeat(${tabGroups.length}, minmax(0, 1fr))` }}>
          {tabGroups.map((g) => {
            const selected = g.id === currentTab || (g.sections.length === 0 && g.id === activeGroup);
            return (
              <button
                key={g.id}
                type="button"
                data-testid={`phone-group-${g.id}`}
                aria-pressed={g.sections.length === 0 ? undefined : selected}
                aria-current={g.sections.length === 0 && selected ? 'page' : undefined}
                onClick={() => {
                  if (g.sections.length === 0) {
                    navigate(g.path ?? '/dashboard');
                    onClose();
                  } else {
                    setTab(g.id);
                  }
                }}
                className={clsx(
                  'h-10 whitespace-nowrap rounded-[9px] px-1 text-[12px]',
                  selected ? 'bg-white font-semibold text-navy-800 shadow-sm' : 'font-medium text-slate-600',
                )}
              >
                {g.id === 'dashboard' ? t('nav.phoneTabDashboard') : t(g.labelKey)}
              </button>
            );
          })}
        </div>

        <nav aria-label={t('nav.sections')} className="flex flex-col">
          {shown?.sections.map((s) => {
            const sectionActive = s.match.some((m) => pathname === m || pathname.startsWith(m + '/'));
            const chips = s.tabs.length >= 2 ? s.tabs : s.chips ?? [];
            return (
              <div key={s.id} className="flex flex-col border-b border-slate-100 py-1">
                <Link
                  to={s.path}
                  onClick={() => {
                    rememberSection(s.groupId, s.id);
                    onClose();
                  }}
                  aria-current={sectionActive ? 'page' : undefined}
                  className="flex min-h-[44px] items-center justify-between text-[15px] font-semibold text-navy-800"
                >
                  {t(s.labelKey)}
                  <CaretRight className="h-4 w-4 text-slate-400" weight="bold" aria-hidden="true" />
                </Link>
                {chips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pb-2">
                    {chips.map((c) => {
                      const active = isTabActive(c, pathname);
                      return (
                        <Link
                          key={c.path}
                          to={c.path}
                          onClick={() => {
                            rememberSection(s.groupId, s.id);
                            onClose();
                          }}
                          aria-current={active ? 'page' : undefined}
                          className={clsx(
                            'inline-flex h-[34px] items-center rounded-full px-3 text-[13.5px]',
                            active
                              ? 'bg-primary-50 font-semibold text-primary-700 ring-1 ring-inset ring-primary-200'
                              : 'bg-white font-medium text-slate-700 ring-1 ring-inset ring-slate-200',
                          )}
                        >
                          {t(c.labelKey)}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      <div className="flex flex-none flex-col gap-3 border-t border-slate-200 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        <div className="flex items-center gap-2.5">
          <Link to="/profile" onClick={onClose} className="flex min-w-0 flex-1 items-center gap-2.5" aria-label={t('nav.profile')}>
            <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
              {initial}
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-semibold text-slate-900">{user?.companyName || user?.contactName || ''}</span>
              <span className="truncate text-[12.5px] text-slate-600">{user?.email}</span>
            </span>
          </Link>
          <span role="group" aria-label={t('nav.language')} className="inline-flex rounded-lg bg-slate-100 p-[3px]">
            {(['en', 'fr'] as const).map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={lang === l}
                onClick={() => {
                  if (lang !== l) onToggleLanguage();
                }}
                className={clsx(
                  'h-[30px] w-10 rounded-md text-[12.5px]',
                  lang === l ? 'bg-white font-semibold text-navy-800' : 'font-medium text-slate-600',
                )}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </span>
        </div>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${nav.admin ? 4 : 3}, minmax(0, 1fr))` }}>
          {nav.admin && (
            <Link to="/admin/users" onClick={onClose} className={footerLink}>
              {t('nav.admin')}
            </Link>
          )}
          <button type="button" onClick={onOpenHelp} className={footerLink}>
            {t('nav.help')}
          </button>
          <button type="button" onClick={onReportBug} className={footerLink}>
            {t('nav.reportBug')}
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex h-10 items-center justify-center rounded-lg bg-danger-50 text-[12.5px] text-danger-700 hover:bg-danger-100"
          >
            {t('nav.logout')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
